'use client';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useRouter } from 'src/routes/hooks';

import { fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';

import HemMeddelandeModal from 'src/sections/karta/components/HemMeddelandeModal';

import type { Kontakt } from 'src/actions/kontakter';
import type { TrädgårdsObjekt } from 'src/components/map/GoogleMap';

// ----------------------------------------------------------------------

type Props = {
  kontakt: Kontakt | null;
  onClose: () => void;
  onDölj: (id: string) => Promise<void>;
  onVäxlaFäst: (id: string) => Promise<void>;
};

/**
 * Kontaktkortet. Åtgärderna skiljer sig åt per typ — ett hem kan man skriva till, en förening
 * kan man gå till, en person kan man mejla eller ringa. Allt renderas som text; ingenting här
 * går genom dangerouslySetInnerHTML eftersom namnen delvis kommer från användare.
 */
export function KontaktKort({ kontakt, onClose, onDölj, onVäxlaFäst }: Props) {
  const router = useRouter();
  const [skrivTillHem, setSkrivTillHem] = useState(false);

  if (!kontakt) {
    return null;
  }

  const gåTill = () => {
    if (kontakt.länk) {
      onClose();
      router.push(kontakt.länk);
    }
  };

  const renderUppgift = (ikon: string, text: string, href: string) => (
    <Box
      component="a"
      href={href}
      sx={{
        gap: 1,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        color: 'text.secondary',
        typography: 'body2',
        textDecoration: 'none',
        '&:hover': { color: 'primary.main' },
      }}
    >
      <Iconify width={18} icon={ikon} />
      {text}
    </Box>
  );

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
          <Avatar
            alt={kontakt.visningsnamn}
            src={kontakt.avatarUrl ?? undefined}
            sx={{ width: 48, height: 48 }}
          >
            <Iconify width={24} icon={kontakt.ikon} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {kontakt.visningsnamn}
            </Typography>
            <Chip size="small" variant="soft" label={kontakt.typEtikett} sx={{ mt: 0.5 }} />
          </Box>
        </DialogTitle>

        <DialogContent>
          {kontakt.underrubrik && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {kontakt.underrubrik}
            </Typography>
          )}

          {/* E-post och telefon visas bara här, aldrig i listan. */}
          {kontakt.epost && renderUppgift('solar:letter-bold', kontakt.epost, `mailto:${kontakt.epost}`)}
          {kontakt.telefon &&
            renderUppgift('solar:phone-bold', kontakt.telefon, `tel:${kontakt.telefon}`)}

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {kontakt.senasteKontakt
              ? `Senaste kontakt för ${fToNow(kontakt.senasteKontakt)} sedan · ${kontakt.antalKontakter} gånger`
              : 'Ni har inte hört av er till varandra här än.'}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {kontakt.id && (
            <Button
              size="small"
              color="inherit"
              startIcon={<Iconify icon={kontakt.fäst ? 'solar:pin-bold' : 'solar:pin-linear'} />}
              onClick={() => onVäxlaFäst(kontakt.id!)}
            >
              {kontakt.fäst ? 'Lossa' : 'Fäst'}
            </Button>
          )}

          {kontakt.id && (
            <Button
              size="small"
              color="error"
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              onClick={async () => {
                await onDölj(kontakt.id!);
                onClose();
              }}
            >
              Ta bort
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {kontakt.typ === 'HEM' && kontakt.kanSkickaMeddelande && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Iconify icon="solar:mailbox-bold" />}
              onClick={() => setSkrivTillHem(true)}
            >
              Skriv till brevlådan
            </Button>
          )}

          {kontakt.typ !== 'HEM' && kontakt.länk && (
            <Button size="small" variant="contained" onClick={gåTill}>
              Öppna
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Återanvänder kartans befintliga brevlådemodal. Den behöver bara punktens id och namn. */}
      {skrivTillHem && (
        <HemMeddelandeModal
          open
          onClose={() => setSkrivTillHem(false)}
          poi={{ id: kontakt.målId, namn: kontakt.visningsnamn } as TrädgårdsObjekt}
          rubrik={kontakt.visningsnamn}
        />
      )}
    </>
  );
}
